import { z } from "zod";

export const knowledgeCrawlStatusSchema = z.enum(["queued", "running", "succeeded", "partial", "failed", "cancelled"]);
export const knowledgeCrawlStopReasonSchema = z.enum(["completed", "max_pages", "max_duration", "budget_exhausted", "circuit_open"]);
export const knowledgeDocumentStatusSchema = z.enum(["indexed", "excluded", "failed", "gone"]);

/**
 * A glob over a URL's path and query. Validated for shape only — what it
 * *matches* is answered by the rule tester, which is a better teacher than
 * any error message could be (`/blog/*` covering `/blog/2026/hello` is a
 * surprise best resolved by showing it).
 */
const patternSchema = z.string().trim().min(1).max(200);

/**
 * The politeness settings, with the ceilings that make them safe to expose.
 *
 * These are the numbers that decide how hard we lean on a client's server,
 * so the bounds matter more than the defaults: 4 requests a second against a
 * small shop's hosting is already generous, and nothing here should let an
 * admin turn the crawler into a load test by typing a bigger number.
 */
const crawlLimitsShape = {
  maxPages: z.number().int().min(1).max(5000).optional(),
  maxDepth: z.number().int().min(0).max(10).optional(),
  maxDurationMinutes: z.number().int().min(1).max(120).optional(),
  requestsPerSecond: z.number().min(0.1).max(4).optional(),
  maxConcurrency: z.number().int().min(1).max(4).optional(),
  respectRobots: z.boolean().optional(),
  followLinksOutsideInclude: z.boolean().optional(),
  refreshIntervalHours: z.number().int().min(1).max(24 * 30).nullable().optional(),
};

export const createKnowledgeSourceBodySchema = z.object({
  rootUrl: z.string().trim().url(),
  includePatterns: z.array(patternSchema).max(50).optional(),
  excludePatterns: z.array(patternSchema).max(50).optional(),
  ...crawlLimitsShape,
});
export type CreateKnowledgeSourceBody = z.infer<typeof createKnowledgeSourceBodySchema>;

export const updateKnowledgeSourceBodySchema = z.object({
  rootUrl: z.string().trim().url().optional(),
  includePatterns: z.array(patternSchema).max(50).optional(),
  excludePatterns: z.array(patternSchema).max(50).optional(),
  ...crawlLimitsShape,
});
export type UpdateKnowledgeSourceBody = z.infer<typeof updateKnowledgeSourceBodySchema>;

export const knowledgeCrawlDtoSchema = z.object({
  id: z.string().uuid(),
  sourceId: z.string().uuid(),
  status: knowledgeCrawlStatusSchema,
  trigger: z.enum(["manual", "scheduled"]),
  stopReason: knowledgeCrawlStopReasonSchema.nullable(),
  stats: z.object({
    pagesFetched: z.number(),
    pagesUnchanged: z.number(),
    pagesSkippedByRule: z.number(),
    pagesFailed: z.number(),
    chunksWritten: z.number(),
    documentsRemoved: z.number(),
    throttledMs: z.number(),
  }),
  costUsd: z.number(),
  error: z.string().nullable(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type KnowledgeCrawlDto = z.infer<typeof knowledgeCrawlDtoSchema>;

export const knowledgeSourceDtoSchema = z.object({
  id: z.string().uuid(),
  rootUrl: z.string(),
  includePatterns: z.array(z.string()),
  excludePatterns: z.array(z.string()),
  followLinksOutsideInclude: z.boolean(),
  maxPages: z.number(),
  maxDepth: z.number(),
  maxDurationMinutes: z.number(),
  requestsPerSecond: z.number(),
  maxConcurrency: z.number(),
  respectRobots: z.boolean(),
  refreshIntervalHours: z.number().nullable(),
  nextRefreshAt: z.string().datetime().nullable(),
  /** Shown and editable in the panel: an admin should be able to read exactly what the bot was told about their client's business. */
  siteBrief: z.string().nullable(),
  siteBriefGeneratedAt: z.string().datetime().nullable(),
  indexedDocuments: z.number(),
  latestCrawl: knowledgeCrawlDtoSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type KnowledgeSourceDto = z.infer<typeof knowledgeSourceDtoSchema>;

export const knowledgeDocumentDtoSchema = z.object({
  id: z.string().uuid(),
  url: z.string(),
  title: z.string().nullable(),
  status: knowledgeDocumentStatusSchema,
  /** Why this page isn't answering questions, in the admin's own vocabulary — "excluded by /checkout*". */
  skipReason: z.string().nullable(),
  tokenCount: z.number(),
  httpStatus: z.number().nullable(),
  fetchedAt: z.string().datetime().nullable(),
  indexedAt: z.string().datetime().nullable(),
});
export type KnowledgeDocumentDto = z.infer<typeof knowledgeDocumentDtoSchema>;

export const updateSiteBriefBodySchema = z.object({ siteBrief: z.string().max(8000).nullable() });
export type UpdateSiteBriefBody = z.infer<typeof updateSiteBriefBodySchema>;

export const setDocumentStatusBodySchema = z.object({ status: z.enum(["indexed", "excluded"]) });
export type SetDocumentStatusBody = z.infer<typeof setDocumentStatusBodySchema>;

/**
 * "What would this crawl do with this URL?" — answered before anything is
 * fetched. The rules are three interacting lists and a discovery flag, and
 * an admin guessing at their combined effect is how a site gets crawled
 * twice.
 */
export const testUrlRulesBodySchema = z.object({
  urls: z.array(z.string().trim().min(1)).min(1).max(20),
  includePatterns: z.array(patternSchema).max(50).optional(),
  excludePatterns: z.array(patternSchema).max(50).optional(),
  followLinksOutsideInclude: z.boolean().optional(),
});
export type TestUrlRulesBody = z.infer<typeof testUrlRulesBodySchema>;

export const testUrlRulesResponseSchema = z.object({
  results: z.array(z.object({ url: z.string(), fetch: z.boolean(), index: z.boolean(), reason: z.string() })),
});
export type TestUrlRulesResponse = z.infer<typeof testUrlRulesResponseSchema>;

/** The retrieval preview: what the bot would find, before a shopper is the one who discovers it wouldn't. */
export const knowledgeSearchBodySchema = z.object({ query: z.string().trim().min(1).max(500), limit: z.number().int().min(1).max(20).optional() });
export type KnowledgeSearchBody = z.infer<typeof knowledgeSearchBodySchema>;

export const knowledgeSearchResponseSchema = z.object({
  results: z.array(
    z.object({
      text: z.string(),
      headingPath: z.string().nullable(),
      sourceUrl: z.string(),
      title: z.string().nullable(),
      score: z.number(),
    }),
  ),
});
export type KnowledgeSearchResponse = z.infer<typeof knowledgeSearchResponseSchema>;
